<div class="manage-collections-panel-menu">
  <div class="manage-collections-panel-header">
    <h3
      [id]="panelTitleId"
      translate="library-collections_manage-collections"
      class="manage-collections-panel-title">
    </h3>
  </div>
  <div class="manage-collections-panel-content">
    <span
      id="manage-collections-grid-instructions"
      class="sr-only"
      translate="library-collections_manage-collections-grid-instructions">
    </span>
    <span
      id="manage-collections-handle-instructions"
      class="sr-only"
      translate="library-collections_manage-collections-handle-instructions">
    </span>

    <table
      accessibleGridReorder
      #collectionGrid="accessibleGridReorder"
      class="manage-collections-grid"
      [accessibleGridReorderItems]="gridItemIds"
      [accessibleGridReorderColumns]="gridColumns"
      accessibleGridReorderInitialTarget="handle"
      accessibleGridReorderPrimaryTarget="handle"
      [accessibleGridReorderLabelledby]="panelTitleId"
      accessibleGridReorderDescribedby="manage-collections-grid-instructions"
      [accessibleGridReorderUseLiveRegionFallback]="true"
      [accessibleGridReorderGetItemLabel]="getCollectionLabel"
      (accessibleGridReorderMoveRequested)="onGridReorderMove($event)"
      (accessibleGridReorderFinalize)="onGridReorderFinalize($event)"
      (accessibleGridReorderCancel)="onGridReorderCancel($event)">
      <thead>
        <tr class="manage-collections-subheader" role="row">
          <th
            [id]="columnHeaderIds.primary"
            scope="col"
            role="columnheader"
            class="manage-collections-subtitle manage-collections-primary-header">
            <span
              aria-hidden="true"
              class="manage-collections-primary-header-spacer">
            </span>
            <span translate="library-collections_manage-collections-subtitle"></span>
          </th>
          <th
            [id]="columnHeaderIds.edit"
            scope="col"
            role="columnheader"
            class="manage-collections-action-header">
            <span class="sr-only" translate="common_edit"></span>
          </th>
          <th
            [id]="columnHeaderIds.delete"
            scope="col"
            role="columnheader"
            class="manage-collections-action-header">
            <span class="sr-only" translate="common_remove"></span>
          </th>
          <th
            [id]="columnHeaderIds.visibility"
            scope="col"
            role="columnheader"
            translate="library-collections_manage-collections-visible"
            class="manage-collections-subtitle manage-collections-visibility-header">
          </th>
        </tr>
      </thead>

      <tbody
        cdkDropList
        role="rowgroup"
        class="collection-cards"
        (cdkDropListDropped)="drop($event)">
        @for (collection of copiedCollections; track collection) {
          <tr
            cdkDrag
            role="row"
            class="collection-card"
            [attr.tabindex]="collectionGrid.getRowTabindex(collection.id)"
            [attr.aria-labelledby]="getCollectionRowHeaderId(collection.id)"
            [attr.data-collection-row-id]="collection.id"
            [attr.data-accessible-grid-row-id]="collection.id"
            (focus)="collectionGrid.handleRowFocus(collection.id)"
            (keydown)="collectionGrid.handleRowKeyDown($event, collection.id)"
            (cdkDragStarted)="onDragStarted()"
            (cdkDragEnded)="onDragEnded()">
            <th
              data-cy="collection-grid-handle-cell"
              data-grid-cell="handle"
              data-accessible-grid-cell="handle"
              scope="row"
              role="rowheader"
              class="collection-card-primary-cell"
              [id]="getCollectionRowHeaderId(collection.id)"
              [attr.tabindex]="collectionGrid.getCellTabindex(collection.id, 'handle')"
              aria-describedby="manage-collections-handle-instructions"
              aria-keyshortcuts="Space Enter ArrowUp ArrowDown Escape"
              (focus)="collectionGrid.handleCellFocus(collection.id, 'handle', $event)"
              (keydown)="collectionGrid.handleReorderCellKeyDown($event, collection.id)">
              <div class="collection-card-primary-content">
                <div
                  cdkDragHandle
                  class="collection-card-handle"
                  [class.selected]="collectionGrid.isGrabbed(collection.id)">
                  <i
                    aria-hidden="true"
                    class="collection-card-drag-handle ficon-app-gripper-dots-vertical">
                  </i>
                </div>
                <span class="collection-card-title collection-card-title-content">
                  {{ collection.name }}
                </span>
              </div>
            </th>

            <td
              [attr.data-grid-cell]="canEditCollection(collection) ? 'edit' : null"
              [attr.data-accessible-grid-cell]="canEditCollection(collection) ? 'edit' : null"
              class="collection-card-action-cell"
              [attr.tabindex]="canEditCollection(collection) ? collectionGrid.getCellTabindex(collection.id, 'edit') : null"
              [attr.headers]="getCollectionCellHeaders(collection.id, columnHeaderIds.edit)"
              (focusin)="canEditCollection(collection) && collectionGrid.handleCellFocus(collection.id, 'edit', $event)"
              (keydown)="canEditCollection(collection) && collectionGrid.handleCellKeyDown($event, collection.id, 'edit')">
              @if (canEditCollection(collection)) {
                <button
                  type="button"
                  class="btn btn-none edit-collection-btn"
                  data-cy="edit-collection-btn"
                  tabindex="-1"
                  [attr.aria-label]="'library-collections_manage-collections-edit' | translate: {collection: collection.name}"
                  (click)="modifyCollection(collection)">
                  <i aria-hidden="true" class="ficon-app-edit"></i>
                </button>
              } @else {
                <span
                  aria-hidden="true"
                  class="collection-card-action-placeholder">
                </span>
              }
            </td>

            <td
              [attr.data-grid-cell]="canDeleteCollection(collection) ? 'delete' : null"
              [attr.data-accessible-grid-cell]="canDeleteCollection(collection) ? 'delete' : null"
              class="collection-card-action-cell"
              [attr.tabindex]="canDeleteCollection(collection) ? collectionGrid.getCellTabindex(collection.id, 'delete') : null"
              [attr.headers]="getCollectionCellHeaders(collection.id, columnHeaderIds.delete)"
              (focusin)="canDeleteCollection(collection) && collectionGrid.handleCellFocus(collection.id, 'delete', $event)"
              (keydown)="canDeleteCollection(collection) && collectionGrid.handleCellKeyDown($event, collection.id, 'delete')">
              @if (canDeleteCollection(collection)) {
                <button
                  type="button"
                  class="btn btn-none remove-collection-btn"
                  data-cy="delete-collection-btn"
                  tabindex="-1"
                  [attr.aria-label]="'library-collections_manage-collections-remove' | translate: {name: collection.name}"
                  (click)="deleteCollection(collection)">
                  <i aria-hidden="true" class="ficon-app-x"></i>
                </button>
              } @else {
                <span
                  aria-hidden="true"
                  class="collection-card-action-placeholder">
                </span>
              }
            </td>

            <td
              [attr.data-grid-cell]="collection.type !== libraryTypes.MINE ? 'visibility' : null"
              [attr.data-accessible-grid-cell]="collection.type !== libraryTypes.MINE ? 'visibility' : null"
              class="collection-card-visibility-cell"
              [attr.tabindex]="collection.type !== libraryTypes.MINE ? collectionGrid.getCellTabindex(collection.id, 'visibility') : null"
              [attr.aria-label]="collection.type !== libraryTypes.MINE ? getVisibilityCellAriaLabel(collection) : null"
              [attr.headers]="getCollectionCellHeaders(collection.id, columnHeaderIds.visibility)"
              (focusin)="collection.type !== libraryTypes.MINE && collectionGrid.handleCellFocus(collection.id, 'visibility', $event)"
              (keydown)="collection.type !== libraryTypes.MINE && collectionGrid.handleCellKeyDown($event, collection.id, 'visibility')">
              @if (collection.type !== libraryTypes.MINE) {
                <mat-checkbox
                  color="primary"
                  class="visibility-check"
                  id="visible-check-{{collection.id}}"
                  [tabIndex]="-1"
                  [checked]="!findMyPermission(collection).is_hidden"
                  (change)="toggleCollectionVisibility(findMyPermission(collection))"
                  [attr.aria-label]="'library-collections_manage-collections-visibility' | translate: {collection: collection.name}">
                </mat-checkbox>
              } @else {
                <span
                  aria-hidden="true"
                  class="visibility-check-placeholder">
                </span>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>

    <button
      type="button"
      (click)="modifyCollection()"
      class="link-btn add-btn add-collection-btn"
      data-cy="add-collection-btn"
      translate="library-collections_manage-collections-add">
    </button>
  </div>
  <footer class="manage-collections-panel-footer">
    <button
      type="button"
      class="tertiary-btn cancel-btn"
      (click)="cancel()"
      translate="common_cancel">
    </button>

    <button
      type="submit"
      class="primary-btn submit-btn"
      [disabled]="saving$ | async"
      (click)="saveCollectionPermissions()"
      translate>
      common_save-and-close
      @if (saving$ | async) {
        <i class="ficon-spinner ficon-spin" aria-live="polite" attr.aria-label="{{'feedback-session_controller-saving'| translate}}"></i>
      }
    </button>
  </footer>
</div>
